import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ArticleCard, { CategoryBadge } from "../components/ArticleCard";
import ShareBar from "../components/ShareBar";
import { api, API, formatDateTime } from "../lib/api";
import { getCategory } from "../lib/categories";
import { Clock, Eye, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [cForm, setCForm] = useState({ author_name: "", content: "" });
  const fetchedSlug = useRef(null);

  useEffect(() => {
    if (fetchedSlug.current === slug) return;
    fetchedSlug.current = slug;
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => {
      setArticle(r.data.article);
      setRelated(r.data.related || []);
      const a = r.data.article;
      document.title = `${a.meta_title || a.title} — Le Véridique`;
      setMeta("description", a.meta_description || a.summary);
      setMeta("og:type", "article", true);
      setMeta("og:site_name", "Le Véridique", true);
      setMeta("og:title", a.meta_title || a.title, true);
      setMeta("og:description", a.meta_description || a.summary, true);
      setMeta("og:image", a.cover_image || "", true);
      setMeta("og:url", window.location.href, true);
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", a.meta_title || a.title);
      setMeta("twitter:description", a.meta_description || a.summary);
      setMeta("twitter:image", a.cover_image || "");
      if (a.allow_comments) {
        api.get(`/articles/${slug}/comments`).then((c) => setComments(c.data));
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!cForm.author_name.trim() || !cForm.content.trim()) return;
    try {
      const r = await api.post(`/articles/${slug}/comments`, cForm);
      setComments([r.data, ...comments]);
      setCForm({ author_name: "", content: "" });
      toast.success("Commentaire publié");
    } catch {
      toast.error("Impossible de publier le commentaire");
    }
  };

  if (notFound) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-28 text-center">
          <h1 className="font-headline text-4xl font-black">Article introuvable</h1>
          <button onClick={() => navigate("/")} className="meta-label text-[#E11D48] mt-4">← Retour à l'accueil</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) return <div className="App"><Header /><div className="py-28 text-center meta-label text-slate-400">Chargement…</div></div>;

  const cat = getCategory(article.category);
  const shareUrl = `${API}/share/${article.category}/${article.slug}`;
  const readMins = Math.max(1, Math.round((article.content || "").split(/\s+/).length / 200));

  return (
    <div className="App">
      <Header />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <nav className="meta-label text-slate-400 mb-4">
          <Link to="/" className="hover:text-[#E11D48]">Accueil</Link> / <Link to={`/rubrique/${cat.slug}`} className="hover:text-[#E11D48]">{cat.name}</Link>
        </nav>
        <CategoryBadge slug={article.category} sub={article.subcategory} />
        <h1 className="font-headline text-3xl sm:text-5xl font-black leading-tight text-[#0A0A0A] mt-3 mb-4" data-testid="article-title">{article.title}</h1>
        <p className="font-body text-xl text-slate-600 leading-relaxed mb-6 text-justify">{article.summary}</p>

        <div className="flex flex-wrap items-center gap-4 py-4 border-y border-[#E2E8F0] mb-6">
          <span className="meta-label text-slate-500 flex items-center gap-1 capitalize"><Clock size={13} />{formatDateTime(article.published_at)}</span>
          <span className="meta-label text-slate-500">· {readMins} min de lecture</span>
          <span className="meta-label text-slate-500 flex items-center gap-1"><Eye size={13} />{article.views}</span>
        </div>

        {(article.cover_image || true) && (
          <img src={article.cover_image || FALLBACK} alt={article.title} className="w-full rounded-lg mb-3" data-testid="article-cover" />
        )}

        <div className="mb-8"><ShareBar title={article.title} url={shareUrl} /></div>

        <div className="article-content dropcap" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />

        {article.author && (
          <p className="font-sub text-right italic text-[#0F172A] font-semibold mt-6" data-testid="article-signature">— {article.author}</p>
        )}

        {article.images?.length > 0 && (
          <div className="mt-8" data-testid="article-gallery">
            <h3 className="font-headline text-xl font-black text-[#0A0A0A] mb-3">Galerie photos</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {article.images.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`gallery-img-${i}`}>
                  <img src={url} alt={`${article.title} ${i + 1}`} className="w-full h-40 object-cover rounded-lg hover:opacity-90 transition-opacity" />
                </a>
              ))}
            </div>
          </div>
        )}

        {article.videos?.length > 0 && (
          <div className="mt-8" data-testid="article-videos">
            <h3 className="font-headline text-xl font-black text-[#0A0A0A] mb-3">Vidéos</h3>
            <div className="grid grid-cols-1 gap-4">
              {article.videos.map((url, i) => (
                <video key={i} src={url} controls className="w-full rounded-lg bg-black" data-testid={`article-video-${i}`} />
              ))}
            </div>
          </div>
        )}

        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#E2E8F0]">
            {article.tags.map((t) => (
              <Link key={t} to={`/recherche?q=${encodeURIComponent(t)}`} className="meta-label bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full text-slate-600">#{t}</Link>
            ))}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#E2E8F0] flex items-center justify-between flex-wrap gap-4">
          <span className="meta-label text-slate-500">Partagez cet article</span>
          <ShareBar title={article.title} url={shareUrl} />
        </div>
      </article>

      {/* Comments */}
      {article.allow_comments && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 border-t border-[#E2E8F0]">
          <h2 className="font-headline text-2xl font-black text-[#0F172A] flex items-center gap-2 mb-6">
            <MessageSquare size={20} /> Commentaires ({comments.length})
          </h2>
          <form onSubmit={submitComment} className="mb-8 space-y-3">
            <input value={cForm.author_name} onChange={(e) => setCForm({ ...cForm, author_name: e.target.value })}
              placeholder="Votre nom" data-testid="comment-name"
              className="w-full border border-[#E2E8F0] rounded-lg px-4 py-2.5 font-sub text-sm outline-none focus:border-[#E11D48]" />
            <textarea value={cForm.content} onChange={(e) => setCForm({ ...cForm, content: e.target.value })}
              placeholder="Votre commentaire…" rows={3} data-testid="comment-content"
              className="w-full border border-[#E2E8F0] rounded-lg px-4 py-2.5 font-sub text-sm outline-none focus:border-[#E11D48]" />
            <button type="submit" data-testid="comment-submit" className="bg-[#0F172A] text-white px-5 py-2.5 rounded-full font-sub text-sm font-semibold flex items-center gap-2">
              <Send size={14} /> Publier
            </button>
          </form>
          <div className="space-y-5">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center font-headline font-bold text-sm shrink-0">{c.author_name.charAt(0).toUpperCase()}</div>
                <div className="bg-slate-50 rounded-lg px-4 py-3 flex-1">
                  <p className="font-sub text-sm font-semibold text-[#0F172A]">{c.author_name}</p>
                  <p className="font-body text-sm text-slate-600 mt-1">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-[#E2E8F0]">
          <h2 className="font-headline text-2xl font-black text-[#0F172A] mb-6">Sur le même sujet</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function setMeta(name, content, property = false) {
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}
